import { useMemo } from 'react';
import type { TreeConditionNode } from '@/components/tree-dnd/tree-dnd-types';
import { ConditionEditSheet } from '@/components/tree-dnd/tree-dnd-conditions/condition-edit-sheet';
import { ConditionEditSheetTargetFooter } from '@/components/tree-dnd/tree-dnd-conditions/condition-edit-sheet-target-footer';
import { ConditionField } from '@/components/tree-dnd/tree-dnd-conditions/condition-field';
import { ConditionNodeSummary } from '@/components/tree-dnd/tree-dnd-conditions/condition-node-summary';
import { NumberPredicateControl } from '@/components/tree-dnd/tree-dnd-conditions/number-predicate-control';
import { useConditionAudiencePreview } from '@/components/tree-dnd/tree-dnd-conditions/use-condition-audience-preview';
import { useTotalSpendSummary } from '@/components/tree-dnd/tree-dnd-conditions/use-condition-summaries';
import { useConditionEditSheet } from '@/components/tree-dnd/use-condition-edit-sheet';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';

interface TotalSpendConditionProps {
  node: Extract<TreeConditionNode, { kind: 'totalSpend' }>;
  onChange: (
    value: Extract<TreeConditionNode, { kind: 'totalSpend' }>['args']
  ) => void;
}

export function TotalSpendCondition({
  node,
  onChange,
}: TotalSpendConditionProps) {
  const { LL, locale } = useI18nContext();
  const { open, onOpenChange } = useConditionEditSheet(node.id);
  const value = node.args;
  const summaryDetail = useTotalSpendSummary(value.amount);
  const conditionLabel = LL.audiences.filters.conditions.totalSpend();

  const filterSummary = useMemo(() => {
    if (node.negated) {
      return LL.audiences.filters.conditionSummary.totalSpendExcludeSummary({
        amount: summaryDetail,
      });
    }
    return LL.audiences.filters.conditionSummary.totalSpendIncludeSummary({
      amount: summaryDetail,
    });
  }, [LL, node.negated, summaryDetail]);

  const preview = useConditionAudiencePreview(node, open);

  return (
    <>
      <ConditionNodeSummary label={conditionLabel} detail={summaryDetail} />
      <ConditionEditSheet
        open={open}
        onOpenChange={onOpenChange}
        title={conditionLabel}
        description={LL.audiences.filters.conditionSummary.totalSpendDescription()}
        footerContent={
          <ConditionEditSheetTargetFooter
            summary={filterSummary}
            targetedUserCount={
              preview.isError ? undefined : preview.targetedUserCount
            }
            totalUserCount={
              preview.isError ? undefined : preview.totalUserCount
            }
            isLoading={preview.isLoading}
            locale={locale as Locales}
          />
        }
      >
        <ConditionField
          label={LL.audiences.filters.editSheet.amountLabel()}
          helpText={LL.audiences.filters.editSheet.totalSpendAmountHelp()}
        >
          <NumberPredicateControl
            value={value.amount}
            onChange={(amount) => onChange({ ...value, amount })}
          />
        </ConditionField>
      </ConditionEditSheet>
    </>
  );
}
